/**
 * SecurityCard — Sitzungs- & Token-Konfiguration
 *
 * • session_timeout_minutes: Inaktivitäts-Auto-Logout (5–720 min)
 * • jwt_expiration_hours:    Token-Lebensdauer ab Login (1–168 h)
 *
 * Werte werden global für alle Benutzer der Firma angewendet.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ShieldCheck } from 'lucide-react';

const SESSION_PRESETS = [
  { v: 30, label: '30 min' },
  { v: 120, label: '2 h' },
  { v: 480, label: '8 h' },
  { v: 540, label: '9 h' },
];

const TOKEN_PRESETS = [
  { v: 8, label: '8 h' },
  { v: 24, label: '24 h' },
  { v: 72, label: '3 Tage' },
  { v: 168, label: '7 Tage' },
];

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

export const SecurityCard = ({ formData, onChange }) => {
  const sessionMin = Number(formData.session_timeout_minutes ?? 30);
  const jwtHours = Number(formData.jwt_expiration_hours ?? 24);

  return (
    <Card className="border-border/60" data-testid="security-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          Sicherheit & Sitzung
        </CardTitle>
        <CardDescription>
          Steuert, wann Benutzer automatisch abgemeldet werden. Greift bei allen Firmen-Mitgliedern.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Inaktivitäts-Logout */}
        <div className="space-y-2">
          <Label htmlFor="session_timeout_minutes">
            Auto-Logout bei Inaktivität (Minuten)
          </Label>
          <Input
            id="session_timeout_minutes"
            type="number"
            min={5}
            max={720}
            step={1}
            value={sessionMin}
            onChange={(e) => onChange('session_timeout_minutes', clamp(parseInt(e.target.value || '0', 10) || 0, 5, 720))}
            data-testid="input-session-timeout"
          />
          <div className="flex flex-wrap gap-2 pt-1">
            {SESSION_PRESETS.map((p) => (
              <button
                key={p.v}
                type="button"
                onClick={() => onChange('session_timeout_minutes', p.v)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  sessionMin === p.v
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:bg-muted'
                }`}
                data-testid={`preset-session-${p.v}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Eingang: 5–720 Minuten. Die Sitzung wird beendet, wenn der Browser keine Maus-, Tastatur-,
            Scroll- oder Touch-Aktivität mehr registriert.
          </p>
        </div>

        {/* Token-Lebensdauer */}
        <div className="space-y-2 pt-4 border-t">
          <Label htmlFor="jwt_expiration_hours">
            Anmelde-Token gültig (Stunden)
          </Label>
          <Input
            id="jwt_expiration_hours"
            type="number"
            min={1}
            max={168}
            step={1}
            value={jwtHours}
            onChange={(e) => onChange('jwt_expiration_hours', clamp(parseInt(e.target.value || '0', 10) || 0, 1, 168))}
            data-testid="input-jwt-hours"
          />
          <div className="flex flex-wrap gap-2 pt-1">
            {TOKEN_PRESETS.map((p) => (
              <button
                key={p.v}
                type="button"
                onClick={() => onChange('jwt_expiration_hours', p.v)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  jwtHours === p.v
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:bg-muted'
                }`}
                data-testid={`preset-jwt-${p.v}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Eingabe: 1–168 Stunden. Spätestens nach Ablauf wird der Anmelde-Token serverseitig
            ungültig und der Nutzer muss sich neu anmelden — auch bei aktiver Nutzung.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SecurityCard;
